import React, { useEffect, useMemo, useState } from 'react';
import { Eye, Package, RefreshCw } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { getAllOrdersAdmin, updateOrderById } from '../services/orderService';

const formatDate = (value) => {
  try {
    if (!value) return '-';
    if (typeof value?.toDate === 'function') {
      return value.toDate().toLocaleString();
    }
    return new Date(value).toLocaleString();
  } catch {
    return String(value || '-');
  }
};

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [selected, setSelected] = useState(null);
  const [isBackfilling, setIsBackfilling] = useState(false);

  const generateOrderNumber = (date = new Date()) => {
    const d = date instanceof Date ? date : new Date(date);
    const pad = (n, len = 2) => String(n).padStart(len, '0');
    const ms = pad(d.getMilliseconds(), 3);
    const extra = pad(Math.floor(Math.random() * 1000), 3);
    return (
      String(d.getFullYear()) +
      pad(d.getMonth() + 1) +
      pad(d.getDate()) +
      pad(d.getHours()) +
      pad(d.getMinutes()) +
      pad(d.getSeconds()) +
      ms +
      extra
    );
  };

  const backfillOrderNumbers = async () => {
    if (isBackfilling) return;
    const targets = (orders || []).filter((o) => o?.id && !o?.orderNumber);
    if (targets.length === 0) {
      toast({ title: 'No update needed', description: 'All orders already have the new Order ID.' });
      return;
    }

    setIsBackfilling(true);
    try {
      for (const o of targets) {
        let d;
        if (typeof o?.createdAt?.toDate === 'function') d = o.createdAt.toDate();
        else if (o?.createdAt) d = new Date(o.createdAt);
        else d = new Date();

        const orderNumber = generateOrderNumber(d);
        await updateOrderById(o.id, { orderNumber });
      }

      toast({
        title: 'Updated',
        description: `Generated new Order IDs for ${targets.length} order(s).`,
      });
      await refresh();
      setSelected((prev) => {
        if (!prev?.id) return prev;
        const updated = (orders || []).find((o) => o?.id === prev.id);
        return updated ? updated : prev;
      });
    } catch (e) {
      toast({ title: 'Backfill failed', description: String(e?.message || 'Please try again.') });
    } finally {
      setIsBackfilling(false);
    }
  };

  const refresh = async () => {
    setIsLoading(true);
    try {
      const loaded = await getAllOrdersAdmin();
      setOrders(Array.isArray(loaded) ? loaded : []);
    } catch (e) {
      toast({ title: 'Failed to load orders', description: String(e?.message || 'Please try again.') });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return orders
      .filter((o) => {
        if (status === 'all') return true;
        return String(o.status || '').toLowerCase() === status;
      })
      .filter((o) => {
        if (!term) return true;
        const id = String(o.id || '').toLowerCase();
        const orderNumber = String(o.orderNumber || '').toLowerCase();
        const email = String(o.email || '').toLowerCase();
        return id.includes(term) || orderNumber.includes(term) || email.includes(term);
      });
  }, [orders, search, status]);

  const updateStatus = async (order, nextStatus) => {
    if (!order?.id) return;
    try {
      await updateOrderById(order.id, { status: nextStatus });
      toast({ title: 'Updated', description: `Order status set to ${nextStatus}.` });
      await refresh();
      setSelected((prev) => (prev && prev.id === order.id ? { ...prev, status: nextStatus } : prev));
    } catch (e) {
      toast({ title: 'Update failed', description: String(e?.message || 'Please try again.') });
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-12">
      <div className="flex flex-col gap-6 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-medium tracking-wide">ORDERS</h1>
          <p className="mt-2 text-sm text-gray-600">Manage customer orders (local now, Firebase later).</p>
        </div>
        <div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={refresh}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm font-medium hover:bg-gray-50 transition-colors"
            >
              <RefreshCw size={16} />
              Refresh
            </button>

            <button
              type="button"
              onClick={backfillOrderNumbers}
              disabled={isBackfilling || isLoading}
              className="inline-flex items-center gap-2 rounded-lg border border-black bg-white px-4 py-3 text-sm font-medium hover:bg-black hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Backfill Order IDs
            </button>
          </div>
        </div>
      </div>

      <div className="mt-8 bg-white border border-gray-200 rounded-2xl p-5 shadow-[0_10px_30px_rgba(0,0,0,0.06)]">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
          <div className="lg:col-span-7">
            <label className="block text-xs text-gray-600">Search</label>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by order id or email"
              className="mt-2 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:border-black transition-colors"
            />
          </div>
          <div className="lg:col-span-5">
            <label className="block text-xs text-gray-600">Status</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="mt-2 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:border-black transition-colors"
            >
              <option value="all">All</option>
              <option value="pending">Pending</option>
              <option value="processing">Processing</option>
              <option value="shipped">Shipped</option>
              <option value="delivered">Delivered</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </div>
      </div>

      <div className="mt-6 bg-white border border-gray-200 rounded-2xl shadow-[0_10px_30px_rgba(0,0,0,0.06)] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr className="text-left text-xs tracking-widest text-gray-600">
                <th className="px-4 py-3">Order</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Total</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Created</th>
                <th className="px-4 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-10 text-center text-sm text-gray-600">
                    Loading orders...
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-sm text-gray-600">
                    No orders found.
                  </td>
                </tr>
              ) : (
                filtered.map((o) => (
                  <tr key={o.id} className="border-b border-gray-200 last:border-b-0">
                    <td className="px-4 py-4 align-top">
                      <div className="text-sm font-medium text-black">{o.orderNumber || o.id}</div>
                      <div className="mt-1 text-xs text-gray-600 inline-flex items-center gap-2">
                        <Package size={14} />
                        {(o.items || []).length} item(s)
                      </div>
                    </td>
                    <td className="px-4 py-4 align-top">
                      <div className="text-sm text-gray-800">{o.email || '-'}</div>
                    </td>
                    <td className="px-4 py-4 align-top">
                      <div className="text-sm text-gray-800">{o.currency ? `${o.currency} ` : ''}{Number(o.total || 0).toFixed(2)}</div>
                    </td>
                    <td className="px-4 py-4 align-top">
                      <div className="inline-flex items-center rounded-full px-3 py-1 text-xs font-medium border border-gray-200 bg-gray-50 text-gray-700">
                        {String(o.status || 'pending').toUpperCase()}
                      </div>
                    </td>
                    <td className="px-4 py-4 align-top">
                      <div className="text-sm text-gray-800">{formatDate(o.createdAt)}</div>
                    </td>
                    <td className="px-4 py-4 align-top">
                      <div className="flex justify-end">
                        <button
                          type="button"
                          onClick={() => setSelected(o)}
                          className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-medium hover:bg-gray-50 transition-colors"
                        >
                          <Eye size={14} />
                          View
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {selected && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center px-6 py-10">
          <button type="button" className="absolute inset-0 bg-black/40" onClick={() => setSelected(null)} aria-label="Close" />
          <div className="relative w-full max-w-2xl bg-white rounded-2xl border border-gray-200 shadow-[0_20px_60px_rgba(0,0,0,0.18)] p-6">
            <div className="flex items-start justify-between gap-6">
              <div>
                <div className="text-sm font-medium tracking-wide">ORDER DETAILS</div>
                <div className="mt-2 text-sm text-gray-700">{selected.orderNumber || selected.id}</div>
                <div className="mt-1 text-xs text-gray-600">Created: {formatDate(selected.createdAt)}</div>
              </div>
              <button
                type="button"
                onClick={() => setSelected(null)}
                className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-medium hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
            </div>

            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="text-xs tracking-widest text-gray-600">CUSTOMER</div>
                <div className="mt-2 text-sm text-gray-800">{selected.email || '-'}</div>
                <div className="mt-2 text-xs tracking-widest text-gray-600">PAYMENT</div>
                <div className="mt-2 text-sm text-gray-800">{selected.paymentMethod || '-'}</div>
              </div>
              <div>
                <div className="text-xs tracking-widest text-gray-600">STATUS</div>
                <select
                  value={String(selected.status || 'pending').toLowerCase()}
                  onChange={(e) => updateStatus(selected, e.target.value)}
                  className="mt-2 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:border-black transition-colors"
                >
                  <option value="pending">Pending</option>
                  <option value="processing">Processing</option>
                  <option value="shipped">Shipped</option>
                  <option value="delivered">Delivered</option>
                  <option value="cancelled">Cancelled</option>
                </select>
                <div className="mt-4 text-xs tracking-widest text-gray-600">TOTAL</div>
                <div className="mt-2 text-sm text-gray-800">{selected.currency ? `${selected.currency} ` : ''}{Number(selected.total || 0).toFixed(2)}</div>
              </div>
            </div>

            <div className="mt-8">
              <div className="text-xs tracking-widest text-gray-600">ITEMS</div>
              <div className="mt-3 space-y-2">
                {(selected.items || []).map((item, idx) => (
                  <div key={`${item.id}-${idx}`} className="flex items-start justify-between gap-4 rounded-xl border border-gray-200 p-4">
                    <div className="flex items-start gap-4">
                      {(() => {
                        const src =
                          (Array.isArray(item?.images) && item.images[0]) ||
                          item?.image ||
                          item?.imageUrl ||
                          '';
                        return src ? (
                          <img
                            src={src}
                            alt={item.name || item.id || 'Product'}
                            className="w-14 h-14 rounded-lg border border-gray-200 object-cover bg-gray-50"
                          />
                        ) : (
                          <div className="w-14 h-14 rounded-lg border border-gray-200 bg-gray-50" />
                        );
                      })()}
                      <div>
                        <div className="text-sm font-medium text-black">{item.name || item.id}</div>
                        <div className="mt-1 text-xs text-gray-600">Size: {item.size || '-'} | Color: {item.color || '-'}</div>
                        <div className="mt-1 text-xs text-gray-600">Qty: {item.quantity || 1}</div>
                      </div>
                    </div>
                    <div className="text-sm text-gray-800">
                      {(() => {
                        const code = String(selected.currency || '').trim();
                        const qty = Number(item.quantity || 1);
                        const prices = item?.prices && typeof item.prices === 'object' ? item.prices : null;
                        const raw = prices && code && Object.prototype.hasOwnProperty.call(prices, code) ? prices[code] : undefined;
                        const unit = Number(raw);
                        const legacy = Number(item.price);
                        const value = Number.isFinite(unit) ? unit * qty : Number.isFinite(legacy) ? legacy * qty : 0;
                        const formatted = code === 'JPY' ? String(Math.round(value)) : value.toFixed(2);
                        return code ? `${code} ${formatted}` : formatted;
                      })()}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-8">
              <div className="text-xs tracking-widest text-gray-600">SHIPPING</div>
              <div className="mt-3 rounded-xl border border-gray-200 p-4 text-sm text-gray-800">
                <div>{selected.shippingAddress?.firstName || ''} {selected.shippingAddress?.lastName || ''}</div>
                <div className="mt-1">{selected.shippingAddress?.address || ''}</div>
                {selected.shippingAddress?.apartment ? <div className="mt-1">{selected.shippingAddress.apartment}</div> : null}
                <div className="mt-1">{selected.shippingAddress?.city || ''}, {selected.shippingAddress?.state || ''} {selected.shippingAddress?.zipCode || ''}</div>
                <div className="mt-1">{selected.shippingAddress?.country || ''}</div>
                <div className="mt-2">Phone: {selected.shippingAddress?.phone || '-'}</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminOrders;
